import { z } from "zod";
import { AccountType, AdvisorId, ClientId, Cusip, Custodian, CustodianAccountId, PortfolioId, ProposalId, Ticker, TradingGroupId, } from "../Types";
/* eslint-disable @typescript-eslint/explicit-function-return-type */
// NOTE: we do have two functions that return schema based on dynamic parameter
// it's better to let TypeScript infer the return type
export const Person = (schema) => z.object({
    id: schema,
    name: z.object({
        first: z.string(),
        middle: z.string().nullable(),
        last: z.string(),
    }),
});
export const Account = z.object({
    custodianAccountId: CustodianAccountId.nullable(),
    custodianName: Custodian,
    displayName: z.string().nullable(),
    accountType: AccountType,
});
export const AccountWithMetadata = z.object({
    info: Account,
    metadata: z.object({
        isAccountBalanceNonPositive: z.boolean().nullable(),
        isAccountCashNonPositive: z.boolean().nullable(),
        isAccountHoldingMissingTicker: z.boolean().nullable(),
    }),
});
export const ProposalInternalStatus = z.enum([
    "CONFIRM",
    "SKIP_PROPOSAL",
    "NEEDS_REVIEW",
    "PROPOSED",
    "SUBMITTED",
    "FAILED",
    "CANCELED",
]);
const ProposalTradingGroupBase = z.object({
    groupId: TradingGroupId,
    groupName: z.string(),
    isActive: z.boolean(),
    isNewerVersionAvailable: z.boolean(),
    taxLossHarvesting: z.boolean(),
});
const ProposalTradingGroupAccount = ProposalTradingGroupBase.extend({
    accounts: Account.array(),
});
const ProposalTradingGroupAccountWithMetadata = ProposalTradingGroupBase.extend({
    accounts: AccountWithMetadata.array(),
});
export const ProposalTradingGroup = z.union([
    ProposalTradingGroupAccount,
    ProposalTradingGroupAccountWithMetadata,
]);
const Proposal = z.object({
    proposalId: ProposalId,
    client: Person(ClientId),
    targetPortfolio: z
        .object({
        fartherId: PortfolioId,
        fartherName: z.string(),
    })
        .nullable(),
    status: z.object({
        internal: ProposalInternalStatus.nullable(),
    }),
    createdOn: z.string().datetime(),
    modifiedOn: z.string().datetime(),
});
export const ListProposal = Proposal.extend({
    tradingGroup: ProposalTradingGroupAccount,
});
export const DetailedProposal = Proposal.extend({
    advisor: Person(AdvisorId),
    tradingGroup: ProposalTradingGroupAccountWithMetadata,
    withTaxBudget: z.boolean(),
});
export const TradeSide = z.enum(["BUY", "SELL"]);
export const Trade = z.object({
    account: Account,
    cusip: Cusip,
    ticker: Ticker.nullable(),
    description: z.string().nullable(),
    side: TradeSide,
    quantity: z.number(),
    price: z.number().nullable(),
    marketValue: z.number().nullable(),
    isInTargetPortfolio: z.boolean(),
});
export const TradeSideSummary = z.object({
    diffTickers: z.number(),
    sideCount: z.number(),
    quantity: z.number(),
    sum: z.number().nullable(),
});
export const ProposedTrades = z.object({
    trades: z.array(Trade),
    summary: z.object({
        totalBuys: TradeSideSummary,
        totalSells: TradeSideSummary,
    }),
});
export const ProposalDetails = z.object({
    activeLiquidations: z
        .array(z.object({
        liquidationId: z.string(),
    }))
        .min(1)
        .nullable(),
    isConfirmable: z.boolean(),
    proposal: DetailedProposal,
    proposedTrades: ProposedTrades,
});
export const PostTradePortfolioItem = z.object({
    account: Account,
    totalQuantity: z.number(),
    totalMarketValue: z.number().nullable(),
    weight: z.number().nullable(),
    price: z.number().nullable(),
});
export const PostTradePortfolioAggregate = z.object({
    cusip: Cusip,
    ticker: Ticker.nullable(),
    description: z.string().nullable(),
    accountItems: z.array(PostTradePortfolioItem).min(1),
    subtotal: z.object({
        totalQuantity: z.number(),
        price: z.number().nullable(),
        totalMarketValue: z.number().nullable(),
        weight: z.number().nullable(),
        targetWeight: z.number(),
        targetWeightDifference: z.number().nullable(),
    }),
});
export const PostTradePortfolioTotal = z.object({
    totalMarketValue: z.number().nullable(),
    weight: z.number().nullable(),
    targetWeight: z.number(),
    targetWeightDifference: z.number().nullable(),
});
//# sourceMappingURL=Types.js.map